import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { ProcedurePinsEditor } from "@/features/fermentation-tank-control/procedure-pins-editor";
import { trpc } from "@/hooks/trpc";
import type {
  FermentationProcedureId,
  CleaningProcedureId,
  EquipmentModelId,
  MediumRecipeId,
} from "@brewbase/db/types";
import { CultivationTypeEnum } from "@brewbase/db/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  XIcon,
  ExternalLinkIcon,
  FlaskConicalIcon,
  PlusIcon,
  ArchiveRestoreIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState, useCallback } from "react";
import {
  UNIT_CATEGORIES,
  type UnitCategory,
  defaultDisplayUnit,
  signalUnitPrefKey,
  unitsOfDimension,
} from "@brewbase/lib";
import { useDebouncedCallback } from "use-debounce";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import Fuse from "fuse.js";
import {
  cumulativeOffsetsH,
  formatHours,
  formatSequenceDue,
} from "@/features/fermentation-procedure-editor/sequence-timing";

/** The dimensions a procedure may pick a signal display unit for, in the order shown. */
const SIGNAL_DIMENSIONS = [
  { dimension: "mass_flow", label: "Mass flow" },
  { dimension: "specific_mass_flow", label: "Specific feed rate" },
  { dimension: "volume_flow", label: "Volume flow" },
  { dimension: "mass", label: "Mass" },
  { dimension: "pressure", label: "Pressure" },
  { dimension: "temperature", label: "Temperature" },
  { dimension: "conductivity", label: "Conductivity" },
];

/**
 * The three weight categories are easy to confuse because they are all grams:
 * what separates them is which side of the vessel the mass is on, so each one
 * says so rather than leaving the operator to guess from the noun.
 */
const UNIT_CATEGORY_LABELS: Record<UnitCategory, { title: string; hint: string }> = {
  tankWeight: {
    title: "Tank Weights",
    hint: "The vessel on the scale — gross weight of the tank itself.",
  },
  additionWeight: {
    title: "Addition Weights",
    hint: "Mass going in: feed bottles and other additions.",
  },
  containerWeight: {
    title: "Collection Containers",
    hint: "Mass coming out: broth and overflow containers, gross of tare.",
  },
  cellConcentration: {
    title: "Cell Concentration",
    hint: "Counts from microscopy and cell counters.",
  },
};

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/overview",
)({
  component: OverviewPage,
});

function OverviewPage() {
  const { procedure_id } = useParams({ from: Route.id });
  const procedureId = procedure_id as unknown as FermentationProcedureId;
  const utils = trpc.useUtils();

  // Fetch procedure data
  const { data: procedure, isLoading } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);

  // Fetch fermentor models for dropdown
  const { data: fermentorModels } =
    trpc.equipment.fermentors.models.list.useQuery();

  // Fetch cleaning record types for cleaning dropdowns
  const { data: cleaningProcedures } = trpc.cleaning.procedures.list.useQuery({
    isActive: true,
  });

  // Medium recipe state
  const [mediumPopoverOpen, setMediumPopoverOpen] = useState(false);
  const [mediumSearch, setMediumSearch] = useState("");

  // Fetch all recipes for selection
  const { data: recipes } = trpc.mediumRecipes.list.useQuery();

  // Fetch the current default recipe
  const { data: currentRecipe, isLoading: isLoadingRecipe } =
    trpc.mediumRecipes.get.useQuery(
      procedure?.default_medium_recipe_id as MediumRecipeId,
      { enabled: !!procedure?.default_medium_recipe_id },
    );

  // Local state for form fields
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");
  const [changelog, setChangelog] = useState("");
  const [fermentorModelId, setFermentorModelId] = useState<string | undefined>(
    undefined,
  );
  const [cultivationType, setCultivationType] = useState<string | undefined>(
    undefined,
  );
  const [preCleaningProcedureId, setPreCleaningProcedureId] = useState<
    CleaningProcedureId | undefined
  >(undefined);
  const [postCleaningProcedureId, setPostCleaningProcedureId] = useState<
    CleaningProcedureId | undefined
  >(undefined);

  const [unitPrefs, setUnitPrefs] = useState<Record<string, string>>({});

  // Update mutation
  const updateMutation = trpc.fermentationProcedures.update.useMutation({
    onSuccess: () => {
      toast.success("Procedure updated");
      utils.fermentationProcedures.getWithDetails.invalidate(procedureId);
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  // Initialize form state when procedure loads
  useEffect(() => {
    if (procedure) {
      setName(procedure.name);
      setVersion(procedure.version);
      setDescription(procedure.description ?? "");
      setChangelog(procedure.changelog ?? "");
      setFermentorModelId(
        procedure.fermentor_model_id?.toString() ?? undefined,
      );
      setCultivationType(procedure.cultivation_type ?? undefined);
      setPreCleaningProcedureId(
        (procedure.required_pre_cleaning_procedure_id ?? undefined) as CleaningProcedureId | undefined,
      );
      setPostCleaningProcedureId(
        (procedure.required_post_cleaning_procedure_id ?? undefined) as CleaningProcedureId | undefined,
      );
      setUnitPrefs(
        (procedure.unit_preferences as Record<string, string>) ?? {},
      );
    }
  }, [procedure]);

  // Debounced save for text fields
  const debouncedSave = useDebouncedCallback(
    (field: string, value: string | null) => {
      if (procedure?.is_locked) return;

      updateMutation.mutate({
        id: procedureId,
        [field]: value,
      });
    },
    500,
  );

  // Handle field changes
  const handleNameChange = (value: string) => {
    setName(value);
    debouncedSave("name", value);
  };

  const handleVersionChange = (value: string) => {
    setVersion(value);
    debouncedSave("version", value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    debouncedSave("description", value || null);
  };

  const handleChangelogChange = (value: string) => {
    setChangelog(value);
    debouncedSave("changelog", value || null);
  };

  const handleFermentorModelChange = (value: string) => {
    setFermentorModelId(value);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      fermentorModelId: parseInt(value) as EquipmentModelId,
    });
  };

  const handleClearFermentorModel = () => {
    setFermentorModelId(undefined);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      fermentorModelId: null,
    });
  };

  const handleCultivationTypeChange = (value: string) => {
    setCultivationType(value);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      cultivationType: value as CultivationTypeEnum,
    });
  };

  const handleClearCultivationType = () => {
    setCultivationType(undefined);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      cultivationType: null,
    });
  };

  const handlePreCleaningProcedureChange = (value: string) => {
    setPreCleaningProcedureId(parseInt(value) as CleaningProcedureId);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      requiredPreCleaningProcedureId: parseInt(value) as CleaningProcedureId,
    });
  };

  const handleClearPreCleaningProcedure = () => {
    setPreCleaningProcedureId(undefined);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      requiredPreCleaningProcedureId: null,
    });
  };

  const handlePostCleaningProcedureChange = (value: string) => {
    setPostCleaningProcedureId(parseInt(value) as CleaningProcedureId);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      requiredPostCleaningProcedureId: parseInt(value) as CleaningProcedureId,
    });
  };

  const handleClearPostCleaningProcedure = () => {
    setPostCleaningProcedureId(undefined);
    if (procedure?.is_locked) return;

    updateMutation.mutate({
      id: procedureId,
      requiredPostCleaningProcedureId: null,
    });
  };

  /** One preference key set or, with null, returned to its default. */
  const setUnitPref = useCallback(
    (key: string, value: string | null) => {
      const updated = { ...unitPrefs };
      if (value === null) delete updated[key];
      else updated[key] = value;
      setUnitPrefs(updated);
      if (procedure?.is_locked) return;
      updateMutation.mutate({ id: procedureId, unitPreferences: updated });
    },
    [unitPrefs, procedure?.is_locked, procedureId, updateMutation],
  );

  // Medium recipe handlers
  const handleSelectRecipe = (recipeId: MediumRecipeId) => {
    updateMutation.mutate({
      id: procedureId,
      default_medium_recipe_id: recipeId,
    });
    setMediumPopoverOpen(false);
  };

  const handleClearRecipe = () => {
    updateMutation.mutate({
      id: procedureId,
      default_medium_recipe_id: null,
    });
    setMediumPopoverOpen(false);
  };

  // Search with Fuse for medium recipes
  const fuse = new Fuse(recipes ?? [], {
    keys: ["name", "version", "description"],
    threshold: 0.3,
  });

  const filteredRecipes = mediumSearch
    ? fuse.search(mediumSearch).map((r) => r.item)
    : (recipes ?? []);

  if (isLoading || !procedure) {
    return (
      <div className="p-6 max-w-3xl space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <Skeleton className="h-32" />
      </div>
    );
  }

  const isArchived = !!procedure.archived_at;
  const isLocked = procedure.is_locked || isArchived;

  const sequence = [...procedure.sequence].sort(
    (a, b) => a.position - b.position,
  );
  const sequenceCumulative = cumulativeOffsetsH(sequence);

  const unarchiveMutation = trpc.fermentationProcedures.unarchive.useMutation({
    onSuccess: () => {
      toast.success("Procedure unarchived.");
      utils.fermentationProcedures.getWithDetails.invalidate(procedureId);
      utils.fermentationProcedures.list.invalidate();
      utils.fermentationProcedures.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to unarchive: ${error.message}`);
    },
  });

  return (
    <div className="p-6 max-w-3xl">
      {isArchived && (
        <div className="flex items-center justify-between bg-olive-2 px-4 py-2 mb-6 text-sm text-olive-11">
          <span>This procedure is archived.</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => unarchiveMutation.mutate(procedureId)}
            disabled={unarchiveMutation.isPending}
          >
            <ArchiveRestoreIcon className="h-3 w-3 mr-1" />
            Unarchive
          </Button>
        </div>
      )}
      <h2 className="text-lg font-semibold mb-8">Procedure Overview</h2>

      {/* Basic Info - Two Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-sm font-medium">
            Name
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            disabled={isLocked}
            placeholder="Procedure name"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="version" className="text-sm font-medium">
            Version
          </Label>
          <Input
            id="version"
            value={version}
            onChange={(e) => handleVersionChange(e.target.value)}
            disabled={isLocked}
            placeholder="e.g., 1.0"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="fermentor-model" className="text-sm font-medium">
            Target Fermentor Model
          </Label>
          <div className="flex items-center gap-2">
            <Select
              value={fermentorModelId}
              onValueChange={handleFermentorModelChange}
              disabled={isLocked}
            >
              <SelectTrigger id="fermentor-model" className="flex-1">
                <SelectValue placeholder="Select model (optional)" />
              </SelectTrigger>
              <SelectContent>
                {fermentorModels?.map((model) => (
                  <SelectItem key={model.id} value={model.id.toString()}>
                    {model.name} - {model.manufacturer}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fermentorModelId && !isLocked && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearFermentorModel}
                className="h-9 w-9 shrink-0"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cultivation-type" className="text-sm font-medium">
            Cultivation Type
          </Label>
          <div className="flex items-center gap-2">
            <Select
              value={cultivationType}
              onValueChange={handleCultivationTypeChange}
              disabled={isLocked}
            >
              <SelectTrigger id="cultivation-type" className="flex-1">
                <SelectValue placeholder="Select type (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="batch">Batch</SelectItem>
                <SelectItem value="fed-batch">Fed-batch</SelectItem>
                <SelectItem value="repeated fed-batch">
                  Repeated fed-batch
                </SelectItem>
                <SelectItem value="continuous">Continuous</SelectItem>
              </SelectContent>
            </Select>
            {cultivationType && !isLocked && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearCultivationType}
                className="h-9 w-9 shrink-0"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Protocol Sequence Summary */}
      <div className="space-y-3 mb-8">
        <div>
          <span className="text-sm font-medium">Protocol sequence</span>
          <p className="text-xs text-muted-foreground mt-1">
            Which protocols a fermentation on this procedure runs, in order,
            and when each is due.
          </p>
        </div>
        {sequence.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <ol className="space-y-1">
            {sequence.map((entry, index) => (
              <li key={entry.id} className="text-sm">
                {entry.position + 1}. {entry.protocolName ?? "Unknown protocol"}
                {entry.protocolVersion && ` v${entry.protocolVersion}`}
                {" · "}
                {formatSequenceDue(entry.position, sequenceCumulative[index] ?? 0)}
                {entry.default_transition_h !== null &&
                  ` · ${formatHours(Number(entry.default_transition_h))}h transition`}
              </li>
            ))}
          </ol>
        )}
        <Link
          to="/fermentations/procedures/$procedure_id/protocols"
          params={{ procedure_id: procedureId }}
          className="inline-flex text-sm text-primary hover:underline"
        >
          Edit sequence
        </Link>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Cleaning Requirements Section */}
      <div className="space-y-3 mb-8">
        <div>
          <span className="text-sm font-medium">Cleaning Requirements</span>
          <p className="text-xs text-muted-foreground mt-1">
            Specify required cleaning procedures before and after fermentations
            using this procedure.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pre-cleaning" className="text-sm font-medium">
              Required Pre-Cleaning
            </Label>
            <div className="flex items-center gap-2">
              <Select
                value={preCleaningProcedureId?.toString()}
                onValueChange={handlePreCleaningProcedureChange}
                disabled={isLocked}
              >
                <SelectTrigger id="pre-cleaning" className="flex-1">
                  <SelectValue placeholder="Select cleaning type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {cleaningProcedures?.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name} ({type.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {preCleaningProcedureId && !isLocked && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClearPreCleaningProcedure}
                  className="h-9 w-9 shrink-0"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="post-cleaning" className="text-sm font-medium">
              Required Post-Cleaning
            </Label>
            <div className="flex items-center gap-2">
              <Select
                value={postCleaningProcedureId?.toString()}
                onValueChange={handlePostCleaningProcedureChange}
                disabled={isLocked}
              >
                <SelectTrigger id="post-cleaning" className="flex-1">
                  <SelectValue placeholder="Select cleaning type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {cleaningProcedures?.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name} ({type.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {postCleaningProcedureId && !isLocked && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClearPostCleaningProcedure}
                  className="h-9 w-9 shrink-0"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Unit Display Preferences */}
      <div className="space-y-3 mb-8">
        <div>
          <span className="text-sm font-medium">Unit Display Preferences</span>
          <p className="text-xs text-muted-foreground mt-1">
            Choose default display units for weight fields. These determine how
            values are shown and entered throughout the fermentation journal.
            Operators can still override per-field.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(
            Object.entries(UNIT_CATEGORIES) as [
              UnitCategory,
              (typeof UNIT_CATEGORIES)[UnitCategory],
            ][]
          ).map(([category, config]) => (
            <div key={category} className="space-y-1.5">
              <Label className="text-sm font-medium">
                {UNIT_CATEGORY_LABELS[category].title}
              </Label>
              <p className="text-xs text-muted-foreground">
                {UNIT_CATEGORY_LABELS[category].hint}
              </p>
              <Select
                value={unitPrefs[category] ?? config.defaultUnit}
                onValueChange={(v) => setUnitPref(category, v)}
                disabled={isLocked}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.allowedUnits.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        {/* Pinned before inoculation */}
        <div className="mt-6 space-y-3">
          <div>
            <span className="text-sm font-medium">Pinned before inoculation</span>
            <p className="text-xs text-muted-foreground mt-1">
              Outputs the operator pins after DO calibration so the vessel holds
              positive pressure until inoculation, which releases them. By
              canonical signal name, so they apply on any rig.
            </p>
          </div>
          <ProcedurePinsEditor
            procedureId={procedureId}
            pins={(procedure.pre_inoculation_pins as Record<string, number>) ?? {}}
            locked={isLocked}
          />
        </div>

        {/* Signal display units */}
        <div className="mt-6 space-y-3">
          <div>
            <span className="text-sm font-medium">Signal Units</span>
            <p className="text-xs text-muted-foreground mt-1">
              How live readings, setpoints and trends are shown and edited on
              this procedure's runs. Applies to every signal of the dimension,
              whatever unit the node reports in.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {SIGNAL_DIMENSIONS.map(({ dimension, label }) => {
              const key = signalUnitPrefKey(dimension);
              const choices = unitsOfDimension(dimension);
              const fallback = defaultDisplayUnit(dimension);
              return (
                <div key={dimension} className="space-y-1.5">
                  <Label className="text-sm font-medium">{label}</Label>
                  <Select
                    value={unitPrefs[key] ?? "__default__"}
                    onValueChange={(v) => setUnitPref(key, v === "__default__" ? null : v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">
                        {fallback ? `Default (${fallback})` : "As reported by the node"}
                      </SelectItem>
                      {choices.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Default Medium Recipe Section */}
      <div className="space-y-3 mb-8">
        <div>
          <Label className="text-sm font-medium">Default Medium Recipe</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Link a default medium recipe to this procedure. When creating new
            fermentations using this procedure, they will automatically be
            assigned this recipe.
          </p>
        </div>
        <div className="space-y-2">
          {!procedure.default_medium_recipe_id ? (
            <div className="border rounded-lg p-4 bg-muted/20 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                No default recipe linked
              </p>
              {!isLocked && (
                <Popover
                  open={mediumPopoverOpen}
                  onOpenChange={setMediumPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1">
                      <PlusIcon className="w-3.5 h-3.5" />
                      Link Recipe
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[320px] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search recipes..."
                        value={mediumSearch}
                        onValueChange={setMediumSearch}
                      />
                      <CommandList>
                        {filteredRecipes.length === 0 && (
                          <CommandEmpty>No recipes found.</CommandEmpty>
                        )}
                        {filteredRecipes.map((recipe) => (
                          <CommandItem
                            key={recipe.id}
                            onSelect={() => handleSelectRecipe(recipe.id)}
                            className="flex items-center gap-2"
                          >
                            <FlaskConicalIcon className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>{recipe.name}</span>
                            <span className="text-xs text-muted-foreground">
                              v{recipe.version}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                    <div className="p-2 border-t">
                      <Link to="/fermentations/medium-recipes">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full gap-1"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          Create New Recipe
                        </Button>
                      </Link>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          ) : isLoadingRecipe ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="border rounded-lg p-4 bg-muted/10">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <FlaskConicalIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">
                      {currentRecipe?.name ?? "Unknown Recipe"}
                    </span>
                    {currentRecipe?.version && (
                      <span className="text-sm text-muted-foreground">
                        v{currentRecipe.version}
                      </span>
                    )}
                  </div>
                  {currentRecipe?.description && (
                    <p className="text-sm text-muted-foreground">
                      {currentRecipe.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!isLocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 hover:text-destructive hover:bg-destructive/10"
                      onClick={handleClearRecipe}
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                {procedure.default_medium_recipe_id && (
                  <Link
                    to="/fermentations/medium-recipes/$recipe_id"
                    params={{
                      recipe_id: procedure.default_medium_recipe_id,
                    }}
                  >
                    <Button variant="outline" size="sm" className="gap-1">
                      <ExternalLinkIcon className="w-3.5 h-3.5" />
                      Open Recipe Editor
                    </Button>
                  </Link>
                )}
                {!isLocked && (
                  <Popover
                    open={mediumPopoverOpen}
                    onOpenChange={setMediumPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm">
                        Change Recipe
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[320px] p-0">
                      <Command>
                        <CommandInput
                          placeholder="Search recipes..."
                          value={mediumSearch}
                          onValueChange={setMediumSearch}
                        />
                        <CommandList>
                          {filteredRecipes.length === 0 && (
                            <CommandEmpty>No recipes found.</CommandEmpty>
                          )}
                          {filteredRecipes.map((recipe) => (
                            <CommandItem
                              key={recipe.id}
                              onSelect={() => handleSelectRecipe(recipe.id)}
                              className="flex items-center gap-2"
                            >
                              <FlaskConicalIcon className="w-3.5 h-3.5 text-muted-foreground" />
                              <span>{recipe.name}</span>
                              <span className="text-xs text-muted-foreground">
                                v{recipe.version}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Description Fields - Full Width */}
      <div className="space-y-6">
        <div className="space-y-1.5">
          <Label htmlFor="description" className="text-sm font-medium">
            Description
          </Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            disabled={isLocked}
            placeholder="Describe this procedure and its purpose..."
            rows={4}
            className="resize-none"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="changelog" className="text-sm font-medium">
            Changelog
          </Label>
          <Textarea
            id="changelog"
            value={changelog}
            onChange={(e) => handleChangelogChange(e.target.value)}
            disabled={isLocked}
            placeholder="What changed in this version..."
            rows={4}
            className="resize-none"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t my-8" />

      {/* Metadata */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          Created {new Date(procedure.created_at).toLocaleDateString()}
        </span>
        <span>
          Updated {new Date(procedure.updated_at).toLocaleDateString()}
        </span>
        {procedure.parent_procedure_id && (
          <span>Derived from procedure #{procedure.parent_procedure_id}</span>
        )}
      </div>
    </div>
  );
}
